/**
 * Writing and showing session notes.
 *
 * A note is opened, changed, and put away from wherever it is read, because the moment to
 * write one down is while the roll call is in front of you. Saving an empty note removes it,
 * which is the same rule that clears an attendance mark.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { dayOf } from '../../shared/dates.js';
import { MAX_NOTE_TEXT_LENGTH } from '../../shared/sync-protocol.js';
import type { IsoDate, Note } from '../../shared/types.js';
import { useToday } from '../clock.js';
import { formatClock, formatMediumDate } from './format.js';
import { CommentIcon, PlusIcon, TrashIcon } from './icons.js';
import { Button } from './primitives.js';

/** Rows the textarea opens at; two lines is enough for the usual one-sentence note. */
const EDITOR_ROWS = 3;

/** Names when a note was last written: the clock for today, the date for anything earlier. */
function writtenAt(timestamp: number, day: IsoDate): string {
  const written = dayOf(timestamp);
  return written === day ? formatClock(timestamp) : formatMediumDate(written);
}

/**
 * Who last wrote a note and when.
 *
 * Rendered as a span rather than a paragraph: a note is shown inside the button that opens
 * it, and a button may only contain phrasing content.
 */
export function NoteByline({ note }: { note: Note }): ReactNode {
  const day = useToday();
  return (
    <span className="mt-1 block truncate text-tiny text-ink-3">
      {note.updatedBy === '' ? '' : `${note.updatedBy} · `}
      {writtenAt(note.updatedAt, day)}
    </span>
  );
}

/**
 * The note editor: a text area, a way to keep it, and a way to leave it alone.
 *
 * Escape cancels and Control-Enter saves, so a note can be written without leaving the
 * keyboard in the middle of a roll call.
 *
 * Removing a note is its own button rather than a line explaining that saving it empty will
 * do the same thing. Both work; only one of them has to be read.
 *
 * @param onSave receives the text as typed; blank text removes the note.
 */
export function NoteForm({
  label,
  value,
  placeholder,
  onSave,
  onCancel,
}: {
  label: string;
  value: string;
  placeholder: string;
  onSave: (text: string) => void;
  onCancel: () => void;
}): ReactNode {
  const [draft, setDraft] = useState(value);
  const field = useRef<HTMLTextAreaElement>(null);
  const id = useId();

  useEffect(() => { field.current?.focus(); }, []);

  const save = (): void => {
    if (draft.trim() !== value.trim()) onSave(draft);
    onCancel();
  };

  return (
    <div className="flex flex-col gap-2">
      <label className="sr-only" htmlFor={id}>{label}</label>
      <textarea
        id={id}
        ref={field}
        rows={EDITOR_ROWS}
        maxLength={MAX_NOTE_TEXT_LENGTH}
        value={draft}
        placeholder={placeholder}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onCancel();
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) save();
        }}
        className="w-full rounded-control border border-line-strong bg-surface p-3 text-base text-ink
          placeholder:text-ink-3"
      />
      <div className="flex items-center gap-2">
        <Button tone="primary" onClick={save}>Save</Button>
        <Button onClick={onCancel}>Cancel</Button>
        {value === '' ? null : (
          <Button
            tone="danger"
            className="ml-auto"
            onClick={() => { onSave(''); onCancel(); }}
          >
            <TrashIcon size={18} />
            Remove
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * The control that opens a note beside whatever the note is about.
 *
 * Narrower and quieter than the attendance control it sits next to: writing a note is the
 * occasional errand, marking attendance is the job. It carries the state of the note in its
 * colour and in its accessible name, never in colour alone.
 */
export function NoteButton({
  label,
  hasNote,
  expanded,
  onClick,
}: {
  /** What the note is about, used to build the accessible name. */
  label: string;
  hasNote: boolean;
  expanded: boolean;
  onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      aria-label={`${hasNote ? 'Change' : 'Write'} the note about ${label}`}
      aria-expanded={expanded}
      onClick={onClick}
      className={`inline-flex h-11 w-9 shrink-0 items-center justify-center rounded-control
        transition-colors hover:bg-sunken
        ${hasNote ? 'text-brand-text' : 'text-ink-3 hover:text-ink'}`}
    >
      <CommentIcon size={19} />
    </button>
  );
}

/**
 * A note and the controls to change it, in one self-contained block.
 *
 * Closed, it is the note itself — pressing it opens the editor — or an invitation to write one
 * when there is nothing yet. Used where the note stands on its own: the session note above a
 * roll call, and each entry in a person's notes.
 */
export function NoteBlock({
  label,
  addLabel,
  placeholder,
  note,
  onSave,
}: {
  /** Accessible name of the field, naming what the note is about. */
  label: string;
  /** Wording of the control shown when there is no note yet. */
  addLabel: string;
  placeholder: string;
  note: Note | undefined;
  onSave: (text: string) => void;
}): ReactNode {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <NoteForm
        label={label}
        value={note?.text ?? ''}
        placeholder={placeholder}
        onSave={onSave}
        onCancel={() => setEditing(false)}
      />
    );
  }

  if (note === undefined) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex h-10 items-center gap-2 text-tiny font-semibold text-brand-text
          underline-offset-2 hover:underline"
      >
        <PlusIcon size={16} />
        {addLabel}
      </button>
    );
  }

  return (
    <button
      type="button"
      aria-label={`${label}. Press to change it.`}
      onClick={() => setEditing(true)}
      className="flex w-full items-start gap-2.5 rounded-control py-1 text-left transition-colors
        hover:bg-sunken"
    >
      <CommentIcon size={18} className="mt-0.5 shrink-0 text-ink-3" />
      <span className="min-w-0 flex-1">
        <span className="block whitespace-pre-wrap text-base text-ink">{note.text}</span>
        <NoteByline note={note} />
      </span>
    </button>
  );
}
